import React from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen, Edit, Trash2, Plus, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { TalentKategori, Talent } from '../../services/api';
import TalentList from './TalentList';

interface TalentTreeProps {
  kategorier: TalentKategori[];
  talenter: Talent[];
  apneKategorier: Set<number>;
  onToggle: (id: number) => void;
  onEditKategori: (kategori: TalentKategori) => void;
  onDeleteKategori: (id: number, navn: string) => void;
  onCreateSubKategori: (parentId: number) => void;
  onCreateTalent: (kategoriId: number) => void;
  onEditTalent: (talent: Talent) => void;
  onDeleteTalent: (id: number, navn: string, kategoriNavn?: string) => void;
  getChildren: (parentId: number) => TalentKategori[];
  getTalenterForKategori: (kategoriId: number) => Talent[];
}

const TalentTree: React.FC<TalentTreeProps> = ({
  kategorier,
  talenter,
  apneKategorier,
  onToggle,
  onEditKategori,
  onDeleteKategori,
  onCreateSubKategori,
  onCreateTalent,
  onEditTalent,
  onDeleteTalent,
  getChildren,
  getTalenterForKategori,
}) => {
  const renderKategori = (kategori: TalentKategori, level: number): React.ReactElement => {
    const children = getChildren(kategori.id);
    const talenterIKategori = getTalenterForKategori(kategori.id);
    const harChildren = children.length > 0;
    const harTalenter = talenterIKategori.length > 0;
    const isExpanded = apneKategorier.has(kategori.id);
    
    // Gradient bakgrunner basert på depth (følger UI_DESIGN_SYSTEM.md)
    const getBgGradient = () => {
      if (level === 0) return 'from-primary to-blue-500';
      if (level === 1) return 'from-purple-500 to-purple-600';
      return 'from-indigo-500 to-indigo-600';
    };

    const getIcon = () => {
      if (level === 0) return <Folder className="h-4 w-4 sm:h-5 sm:w-5" />;
      if (level === 1) return <FolderOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />;
      return <Tag className="h-3 w-3 sm:h-3.5 sm:w-3.5" />;
    };

    return (
      <div key={kategori.id} className="mb-2 sm:mb-3">
        <Collapsible open={isExpanded} onOpenChange={() => onToggle(kategori.id)}>
          {/* Kategori Header */}
          <CollapsibleTrigger asChild>
            <div
              className={cn(
                'cursor-pointer transition-all rounded-lg overflow-hidden',
                'bg-gradient-to-r hover:shadow-md',
                getBgGradient()
              )}
            >
              <div className={cn(
                'flex flex-col sm:flex-row sm:items-center gap-2 text-white',
                level === 0 ? 'p-3 sm:p-4' : 'p-2 sm:p-3'
              )}>
                {/* Venstre side: Chevron + Ikon + Navn */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20 text-white shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle(kategori.id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />
                    ) : (
                      <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </Button>
                  
                  {getIcon()}
                  
                  <h3 className={cn(
                    'font-semibold truncate',
                    level === 0 ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
                  )}>
                    {kategori.navn}
                  </h3>
                </div>

                {/* Høyre side: Badges + Action buttons */}
                <div className="flex items-center gap-1 sm:gap-2 flex-wrap ml-9 sm:ml-0">
                  {/* Badges */}
                  {harChildren && (
                    <Badge 
                      variant="secondary"
                      className="bg-white/20 text-white border-white/30 text-xs shrink-0"
                    >
                      {children.length} sub
                    </Badge>
                  )}
                  {harTalenter && (
                    <Badge 
                      variant="secondary"
                      className="bg-white/20 text-white border-white/30 text-xs shrink-0"
                    >
                      {talenterIKategori.length} {talenterIKategori.length === 1 ? 'talent' : 'talenter'}
                    </Badge>
                  )}
                  
                  {/* Action buttons - kompakt på mobil */}
                  <Button 
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 sm:h-8 sm:w-8 hover:bg-white/20 text-white shrink-0"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      onEditKategori(kategori); 
                    }}
                  >
                    <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                  
                  <Button 
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 sm:h-8 sm:w-8 hover:bg-white/20 text-white shrink-0"
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      onDeleteKategori(kategori.id, kategori.navn); 
                    }}
                  >
                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                  
                  {/* Legg til sub-kategori (kun hvis IKKE har talenter) */}
                  {!harTalenter && (
                    <Button 
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-8 sm:w-8 hover:bg-white/20 text-white shrink-0"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        onCreateSubKategori(kategori.id); 
                      }}
                      title="Legg til sub-kategori"
                    >
                      <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  )}
                  
                  {/* Legg til talent (kun hvis IKKE har children) */}
                  {!harChildren && (
                    <Button 
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 sm:h-8 sm:w-8 hover:bg-white/20 text-white shrink-0"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        onCreateTalent(kategori.id); 
                      }}
                      title="Legg til talent"
                    >
                      <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CollapsibleTrigger>

          {/* Kategori Content */}
          <CollapsibleContent>
            <div className="pl-4 sm:pl-6 mt-2 sm:mt-3">
              {/* Sub-kategorier (rekursivt) */}
              {children.map(child => renderKategori(child, level + 1))}
              
              {/* Talenter for denne kategorien */}
              {harTalenter && (
                <div className="mt-2 sm:mt-3">
                  <TalentList 
                    talenter={talenterIKategori}
                    onEdit={onEditTalent}
                    onDelete={onDeleteTalent}
                  />
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  };

  const rootKategorier = kategorier.filter(k => !k.parent_id);

  return (
    <div className="space-y-2 sm:space-y-3">
      {rootKategorier.map(root => renderKategori(root, 0))}
    </div>
  );
};

export default TalentTree;

